import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { rolesAPI } from '../utils/api';
import { toast } from 'sonner';
import { LayoutDashboard, ClipboardList, Users, Menu, Settings, Shield, Sliders, Building2, FolderKanban, ChevronRight } from 'lucide-react';

const EditRolDialog = ({ rol, menus, open, onClose }) => {
  const [formData, setFormData] = useState({
    nombre: rol.nombre,
    descripcion: rol.descripcion || '',
    permisos: rol.permisos || [],
  });
  const [loading, setLoading] = useState(false);

  // Icon mapping
  const getIconComponent = (iconName) => {
    const iconMap = {
      'LayoutDashboard': LayoutDashboard,
      'ClipboardList': ClipboardList,
      'Users': Users,
      'Menu': Menu,
      'Settings': Settings,
      'Shield': Shield,
      'Sliders': Sliders,
      'Building2': Building2,
      'FolderKanban': FolderKanban,
    };
    return iconMap[iconName] || LayoutDashboard;
  };

  // Organize menus hierarchically in sidebar order
  const organizedMenus = useMemo(() => {
    if (!menus || menus.length === 0) return [];

    // Separate parent and child menus
    const parentMenus = menus.filter(m => !m.padre_id);
    const childMenus = menus.filter(m => m.padre_id);

    // Build hierarchical structure sorted by orden
    const structured = [];
    
    parentMenus
      .sort((a, b) => (a.orden || 0) - (b.orden || 0))
      .forEach(parent => {
        // Add parent menu
        structured.push({
          ...parent,
          isParent: true,
          level: 0
        });
        
        // Add children of this parent
        const children = childMenus
          .filter(c => c.padre_id === parent.id)
          .sort((a, b) => (a.orden || 0) - (b.orden || 0));
        
        children.forEach(child => {
          structured.push({
            ...child,
            isParent: false,
            level: 1
          });
        });
      });

    return structured;
  }, [menus]);

  const handlePermissionChange = (menuId, action) => {
    setFormData(prev => {
      const permisos = [...prev.permisos];
      const existingIndex = permisos.findIndex(p => p.menu_id === menuId);
      
      if (existingIndex >= 0) {
        permisos[existingIndex] = {
          ...permisos[existingIndex],
          [action]: !permisos[existingIndex][action]
        };
      } else {
        permisos.push({
          menu_id: menuId,
          ver: action === 'ver',
          crear: action === 'crear',
          editar: action === 'editar',
          eliminar: action === 'eliminar'
        });
      }
      
      return { ...prev, permisos };
    });
  };

  const getPermission = (menuId, action) => {
    // El rol Administrador siempre tiene todos los permisos
    if (rol.nombre === 'Administrador') {
      return true;
    }
    const permiso = formData.permisos.find(p => p.menu_id === menuId);
    return permiso ? permiso[action] : false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await rolesAPI.update(rol.id, formData);
      toast.success('Rol actualizado exitosamente');
      onClose();
    } catch (error) {
      toast.error('Error al actualizar rol');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Editar Rol
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nombre del Rol *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                className="border-[#c5b8a2]"
                required
                disabled={rol.nombre === 'Administrador'}
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                className="border-[#c5b8a2]"
              />
            </div>
          </div>

          <div>
            <Label className="mb-3 block text-lg font-semibold">Permisos por Menú</Label>
            <div className="border border-[#E8E8E7] rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F5F5F5]">
                  <tr>
                    <th className="text-left p-3 text-sm font-semibold text-[#2D2D2D]">Menú</th>
                    <th className="text-center p-3 text-sm font-semibold text-[#2D2D2D] w-20">Ver</th>
                    <th className="text-center p-3 text-sm font-semibold text-[#2D2D2D] w-20">Crear</th>
                    <th className="text-center p-3 text-sm font-semibold text-[#2D2D2D] w-20">Editar</th>
                    <th className="text-center p-3 text-sm font-semibold text-[#2D2D2D] w-20">Eliminar</th>
                  </tr>
                </thead>
                <tbody>
                  {organizedMenus.map((menu) => {
                    const Icon = getIconComponent(menu.icono);
                    return (
                      <tr 
                        key={menu.id} 
                        className={`border-t border-[#E8E8E7] hover:bg-[#F5F5F5]/50 ${
                          menu.isParent ? 'bg-[#FAFAFA]' : ''
                        }`}
                      >
                        <td className="p-3">
                          <div 
                            className={`flex items-center gap-2 ${
                              menu.level === 1 ? 'ml-6' : ''
                            }`}
                          >
                            {menu.level === 1 && (
                              <ChevronRight size={14} className="text-[#888888]" />
                            )}
                            <Icon 
                              size={menu.isParent ? 18 : 16} 
                              className={menu.isParent ? 'text-[#9d8272]' : 'text-[#888888]'} 
                            />
                            <span className={`text-sm ${
                              menu.isParent 
                                ? 'font-semibold text-[#2D2D2D]' 
                                : 'text-[#5A5A5A]'
                            }`}>
                              {menu.nombre}
                            </span>
                          </div>
                        </td>
                        {['ver', 'crear', 'editar', 'eliminar'].map((action) => (
                          <td key={action} className="p-3 text-center">
                            <Checkbox
                              checked={getPermission(menu.id, action)}
                              onCheckedChange={() => handlePermissionChange(menu.id, action)}
                              className="border-[#c5b8a2] data-[state=checked]:bg-[#9d8272] data-[state=checked]:border-[#9d8272]"
                              disabled={rol.nombre === 'Administrador'}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[#E8E8E7]">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditRolDialog;
